/**
 * The one way this app calls its own authenticated endpoints.
 *
 * Same-origin, so no deploy token and no CORS — but always a Bearer token: with
 * no signed-in user there is nothing to ask for, and sending the request anyway
 * would just spend a rate-limit slot on a guaranteed 401.
 *
 * Lifted out of favorites.service.ts when preferences became a second caller;
 * the behaviour is unchanged, including that the body is parsed on failures too
 * (a 409 from the tag rename carries the colliding tag's id, which is what
 * turns "that name is taken" into an offer to merge).
 */

import { authHeader } from './auth-token'

export type ApiResult<T> =
   | { ok: true; data: T }
   | { ok: false; error: string; status: number; body: Record<string, unknown> }

export async function authedFetch<T>(
   path: string,
   init: RequestInit = {},
   // Only for the console line when the network itself fails, so a reader of
   // the log can tell which feature was talking.
   label = 'api'
): Promise<ApiResult<T>> {
   const auth = await authHeader()
   if (!auth.Authorization) {
      return { ok: false, error: 'unauthenticated', status: 401, body: {} }
   }

   try {
      const response = await fetch(path, {
         ...init,
         headers: {
            ...(init.body ? { 'Content-Type': 'application/json' } : {}),
            ...auth,
            ...init.headers,
         },
      })

      const body: unknown = await response.json().catch(() => ({}))
      const record =
         typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : {}

      if (!response.ok) {
         const error = 'error' in record ? String(record.error) : 'server_error'
         return { ok: false, error, status: response.status, body: record }
      }
      return { ok: true, data: body as T }
   } catch (err) {
      console.error(`[${label}] request failed`, path, err)
      return { ok: false, error: 'network_error', status: 0, body: {} }
   }
}
